<script setup lang="ts">
import Dialog from 'primevue/dialog';
import { AppTypography } from '~/components/base';
import { useAutoHideScrollbar } from '~/composables/useAutoHideScrollbar';

const { options = [] } = defineProps<{
  options?: { label: string; value: string }[];
}>();

const visible = defineModel<boolean>('visible', { default: false });
const modelValue = defineModel<string>({ default: '' });

const { isScrolling, onScroll: onListScroll } = useAutoHideScrollbar();

const select = (value: string) => {
  modelValue.value = value;
  visible.value = false;
};
</script>

<template>
  <Dialog
    v-model:visible="visible"
    :draggable="false"
    modal
    header="Party"
    class="app-modal candidate-party w-full sm:max-w-md"
    :dismissable-mask="true"
  >
    <div class="candidate-party__content flex flex-col gap-4">
      <ul
        class="candidate-party__list flex flex-col"
        :class="{ 'candidate-party__list--scrolling': isScrolling }"
        @scroll.passive="onListScroll"
      >
        <li
          v-for="option in options"
          :key="option.value"
          :data-testid="`candidate-party-${option.value}`"
          class="candidate-party__row flex cursor-pointer items-center justify-between py-3"
          role="button"
          tabindex="0"
          @click="select(option.value)"
          @keydown.enter="select(option.value)"
          @keydown.space.prevent="select(option.value)"
        >
          <AppTypography type="body">{{ option.label }}</AppTypography>
          <Icon
            v-if="modelValue === option.value"
            name="solar:check-circle-bold"
            class="candidate-party__check"
          />
        </li>
      </ul>
    </div>
  </Dialog>
</template>

<!--
  The dialog is teleported to <body> and carries no scoped data-v attribute,
  so scoped :deep() can't reach its chrome (header/content/panel). The
  `.candidate-party` class *is* on the .p-dialog panel, so the sheet chrome is
  themed in the unscoped block below; MY slot content (rows) keeps its scoped
  styles here.
-->
<style scoped lang="scss">
.candidate-party {
  &__content {
    // Stretch into the mobile full-screen sheet so the list below can take the
    // leftover height instead of leaving dead space at the bottom.
    @media (max-width: 639.98px) {
      height: 100%;
    }
  }

  &__list {
    // The party list is unbounded (one row per party), so it scrolls inside the
    // dialog rather than pushing the panel past the viewport.
    max-height: min(26rem, 60vh);
    overflow-y: auto;

    @include auto-hide-scrollbar;

    @media (max-width: 639.98px) {
      flex: 1 1 auto;
      min-height: 0;
      max-height: none;
    }
  }

  &__row {
    // Warm tan divider that reads on the sand sheet, matching the other
    // filter sheets.
    border-bottom: 1px solid $complementary-color-shade-1;

    &:last-of-type {
      border-bottom: none;
    }
  }

  &__check {
    color: $text-color-brand;
    font-size: 1.25rem;
  }
}
</style>

<!--
  Unscoped: themes the teleported PrimeVue dialog chrome via the
  `.candidate-party` class it carries on the .p-dialog panel. Kept narrow to
  this modal so it doesn't leak to other dialogs.
-->
<style lang="scss">
.candidate-party.p-dialog {
  // Warm sand sheet, matching the location picker and the other filter sheets.
  background-color: #ece3d1;

  .p-dialog-header,
  .p-dialog-content {
    background-color: #ece3d1;
  }

  // Centered title with a plain (un-circled) close button pinned to the right.
  .p-dialog-header {
    position: relative;
    justify-content: center;
  }

  .p-dialog-title {
    text-align: center;
  }

  .p-dialog-close-button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    border: none;
    background: transparent;
    box-shadow: none;

    // Bare X, no background, on the mobile full-screen sheet — coloured to
    // match every other filter modal's mobile close button (Figma's
    // text/primary on this sand sheet).
    @media (max-width: 639.98px) {
      color: $text-color-subtext;
    }

    // Desktop: persistent light circle behind the X, at rest — matching the
    // other Filters modals (Campaign/PartyFilterModal). PrimeVue's default
    // close button only paints this on `:hover`; Figma shows it
    // unconditionally.
    @media (min-width: 640px) {
      width: 28px;
      height: 28px;
      padding: 0;
      color: $border-color-tag;
      background-color: $surface-color-bg-2;

      &:hover,
      &:enabled:hover {
        background-color: $surface-color-bg-2;
      }
    }
  }
}
</style>
